import React, { forwardRef, useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useChatStore from "../../../stores/chatStore";
import useAppStore from "../../../stores/appStore";
import type { InlineButton } from "../../../axios/chat";
import { notifyOfflineActionBlocked } from "../../../utils/internetConnection";
import {
  getBackButtonSx,
  getBackIconSx,
  getBackLabelSx,
  getChildHintButtonSx,
  getChildHintIconCircleSx,
  getChildHintLabelSx,
  getChildHintLeadIconSx,
  getChildrenGridSx,
  getHintButtonContainerSx,
  getHintLabelSx,
  getHintLeadIconSx,
  getLevel1RowSx,
  getLevel2HeaderRowSx,
  getMenuTitleSx,
  getShellSx,
  getSkipButtonSx,
  getSubtitleSx,
  type QuickHintsStyleParams,
} from "./styles";

export interface FooterQuickHintsProps {
  onSelectPrompt: (prompt: string) => void;
  hidden?: boolean;
}

const LEADING_EMOJI_RE =
  /^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)\s*/u;

function parseHintTitle(title: string): { icon: string | null; label: string } {
  const t = title.trim();
  if (!t) return { icon: null, label: "" };

  const emojiMatch = t.match(LEADING_EMOJI_RE);
  if (emojiMatch) {
    const icon = emojiMatch[1];
    const label = t.slice(emojiMatch[0].length).trim();
    return { icon, label: label || t };
  }

  const symbolMatch = t.match(/^(\S{1,3})\s+(.+)$/);
  if (symbolMatch && !/^\p{Letter}+$/u.test(symbolMatch[1])) {
    return { icon: symbolMatch[1], label: symbolMatch[2].trim() };
  }

  try {
    const segmenter = new Intl.Segmenter(undefined, {
      granularity: "grapheme",
    });
    const segments = [...segmenter.segment(t)];
    const first = segments[0]?.segment ?? "";
    if (first && /\p{Extended_Pictographic}/u.test(first)) {
      const label = segments
        .slice(1)
        .map((s) => s.segment)
        .join("")
        .trim();
      return { icon: first, label: label || t };
    }
  } catch {
    //
  }

  return { icon: null, label: t };
}

function stripLeadingEmojiFromLabel(label: string): string {
  const trimmed = label.trim();
  const match = trimmed.match(LEADING_EMOJI_RE);
  return match ? trimmed.slice(match[0].length).trim() : trimmed;
}

function hintLeadAndLabel(
  title: string,
  styleParams: QuickHintsStyleParams,
): { lead: React.ReactElement | null; label: string } {
  const { icon, label } = parseHintTitle(title);
  if (!icon) return { lead: null, label };

  return {
    lead: (
      <Box aria-hidden component="span" sx={getHintLeadIconSx(styleParams)}>
        {icon}
      </Box>
    ),
    label,
  };
}

function formatLabelTwoLines(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return label.trim();
  const mid = Math.ceil(words.length / 2);
  return `${words.slice(0, mid).join(" ")}\n${words.slice(mid).join(" ")}`;
}

function childHintLeadAndLabel(
  title: string,
  styleParams: QuickHintsStyleParams,
): { lead: React.ReactElement | null; label: string } {
  let { icon, label } = parseHintTitle(title);

  if (!icon) {
    const retry = parseHintTitle(label);
    if (retry.icon) {
      icon = retry.icon;
      label = retry.label;
    }
  }

  const cleanLabel = stripLeadingEmojiFromLabel(label || title.trim());
  const displayLabel = formatLabelTwoLines(cleanLabel);

  return {
    lead: (
      <Box component="span" sx={getChildHintIconCircleSx(styleParams)}>
        {icon ? (
          <Box component="span" sx={getChildHintLeadIconSx(styleParams)}>
            {icon}
          </Box>
        ) : null}
      </Box>
    ),
    label: displayLabel,
  };
}

interface HintButtonProps {
  btn: InlineButton;
  styleParams: QuickHintsStyleParams;
  disabled: boolean;
  compact?: boolean;
  onClick: () => void;
}

const HintButton = ({
  btn,
  styleParams,
  disabled,
  compact,
  onClick,
}: HintButtonProps) => {
  const { lead, label } = hintLeadAndLabel(btn.title, styleParams);

  return (
    <Box
      component="button"
      type="button"
      aria-label={btn.title}
      disabled={disabled}
      onClick={onClick}
      sx={getHintButtonContainerSx(styleParams, { compact, disabled })}
    >
      {lead}
      <Typography component="span" sx={getHintLabelSx(styleParams, compact)}>
        {label}
      </Typography>
    </Box>
  );
};

interface ChildHintButtonProps {
  btn: InlineButton;
  styleParams: QuickHintsStyleParams;
  disabled: boolean;
  onClick: () => void;
}

const ChildHintButton = ({
  btn,
  styleParams,
  disabled,
  onClick,
}: ChildHintButtonProps) => {
  const { lead, label } = childHintLeadAndLabel(btn.title, styleParams);

  return (
    <Box
      component="button"
      type="button"
      aria-label={btn.title}
      disabled={disabled}
      onClick={onClick}
      sx={getChildHintButtonSx(styleParams, disabled)}
    >
      {lead}
      <Typography component="span" sx={getChildHintLabelSx(styleParams)}>
        {label}
      </Typography>
    </Box>
  );
};

export const FooterQuickHints = forwardRef<
  HTMLDivElement,
  FooterQuickHintsProps
>(({ onSelectPrompt, hidden }, ref) => {
  const { inlineButtons, inlineMenuTitle, fetchInlineButtons } = useChatStore();
  const { isPreassessmentOpen, focus, domain, isOnline } = useAppStore();
  const theme = useTheme();
  const styleParams: QuickHintsStyleParams = {
    isDark: theme.palette.mode === "dark",
    isSmallScreen: useMediaQuery(theme.breakpoints.down("sm")),
    isTabletScreen: useMediaQuery(theme.breakpoints.down("md")),
  };
  const [activeParent, setActiveParent] = useState<InlineButton | null>(null);
  const hintsDisabled = isPreassessmentOpen || !isOnline;

  useEffect(() => {
    fetchInlineButtons();
  }, [fetchInlineButtons, focus, domain]);

  useEffect(() => {
    setActiveParent(null);
  }, [inlineButtons, focus, domain]);

  const handleChildButtonClick = (btn: InlineButton) => {
    if (hintsDisabled) {
      if (!isOnline) notifyOfflineActionBlocked();
      return;
    }
    const prompt = btn.promt_example?.trim();
    if (!prompt) return;
    onSelectPrompt(prompt);
    setActiveParent(null);
  };

  const handleParentClick = (btn: InlineButton) => {
    if (hintsDisabled) {
      if (!isOnline) notifyOfflineActionBlocked();
      return;
    }
    const children = btn.children?.filter((c) => c.title?.trim());
    if (children && children.length > 0) {
      setActiveParent(btn);
      return;
    }
    handleChildButtonClick(btn);
  };

  const closeLevel2Menu = () => {
    if (hintsDisabled) return;
    setActiveParent(null);
  };

  const handleSkip = () => {
    if (hintsDisabled) {
      if (!isOnline) notifyOfflineActionBlocked();
      return;
    }
    const text = activeParent?.subtitle?.trim();
    if (text) {
      onSelectPrompt(text);
    }
    setActiveParent(null);
  };

  if (hidden || inlineButtons.length === 0) {
    return null;
  }

  const children = activeParent?.children?.filter((c) => c.title?.trim()) ?? [];

  if (activeParent && children.length > 0) {
    return (
      <Box ref={ref} sx={getShellSx(styleParams)}>
        <Box sx={getLevel2HeaderRowSx(styleParams)}>
          <Box
            component="button"
            type="button"
            aria-label="Back"
            disabled={hintsDisabled}
            onClick={closeLevel2Menu}
            sx={getBackButtonSx(styleParams, hintsDisabled)}
          >
            <ArrowBackIcon sx={getBackIconSx(styleParams)} />
            <Typography component="span" sx={getBackLabelSx(styleParams)}>
              Back
            </Typography>
          </Box>

          {activeParent.subtitle?.trim() ? (
            <Typography sx={getSubtitleSx(styleParams)} noWrap>
              {activeParent.subtitle}
            </Typography>
          ) : (
            <Box sx={{ flex: 1, minWidth: 0 }} />
          )}

          <Box
            component="button"
            type="button"
            aria-label="Skip"
            disabled={hintsDisabled}
            onClick={handleSkip}
            sx={getSkipButtonSx(styleParams, hintsDisabled)}
          >
            Skip
          </Box>
        </Box>

        <Box sx={getChildrenGridSx(styleParams)}>
          {children.map((btn, index) => (
            <ChildHintButton
              key={`${btn.id ?? btn.title}-${index}`}
              btn={btn}
              styleParams={styleParams}
              disabled={hintsDisabled}
              onClick={() => handleChildButtonClick(btn)}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={ref} sx={getShellSx(styleParams)}>
      {inlineMenuTitle?.trim() ? (
        <Typography sx={getMenuTitleSx(styleParams)}>
          {inlineMenuTitle}
        </Typography>
      ) : null}

      <Box sx={getLevel1RowSx(styleParams)}>
        {inlineButtons.map((btn, index) => (
          <HintButton
            key={`${btn.id ?? btn.title}-${index}`}
            btn={btn}
            styleParams={styleParams}
            disabled={hintsDisabled}
            onClick={() => handleParentClick(btn)}
          />
        ))}
      </Box>
    </Box>
  );
});
